import Text from '@raydiant/raydial/components/Text';
import PresentationCard from './PresentationCard';
import Button from '@raydiant/raydial/components/Button';
import DisplaiAcademyThumbnail from './DisplaiAcademyThumbnail.png';
import ImagesThumbnail from './ImagesThumbnail.svg';
import InstagramThumbnail from './InstagramThumbnail.svg';
import MenusThumbnail from './MenusThumbnail.svg';
import { useSelector } from 'react-redux';
import {
  selectIsLiteUser,
  selectUserProfile,
} from '../../../../../selectors/user';
import StackedLibrariesGlyph from './StackedLibrariesGlyph';

const SHARED_LIBRARIES_HELP_URL =
  'https://help.displai.ai/getting-started/5imknKqrcfswGZ9RmtHfPK/dashboard-introduction-and-basics/5imknKqrcinJNiXdEoT8cC';

export interface EmptyLibraryPros {
  onNewPresentation: () => void;
  onPresentationCardClick: (applicationId: string) => void;
  onFileUpload: () => void;
  createButtonLabel?: string;
  isSharedLibrary?: boolean;
}

const INSTAGRAM_APP_ID = 'a0853ff6-f790-4743-80fe-246e9d29eba1';
const MENUS_APP_ID = 'ec2710b0-78a8-4dcb-aaf9-e45f465ed99e';

const EmptyLibrary = ({
  onPresentationCardClick,
  onNewPresentation,
  onFileUpload,
  createButtonLabel = 'Create a Presentation',
  isSharedLibrary = false,
}: EmptyLibraryPros) => {
  const currentUser = useSelector(selectUserProfile);
  const isLiteUser = useSelector(selectIsLiteUser);

  if (!currentUser) return null;

  // Dedicated empty state for the Shared Libraries section: illustrated hero
  // with a single primary CTA and an inline learn-more link.
  if (isSharedLibrary) {
    return (
      <div className="min-h-[490px] flex flex-1 flex-col items-center text-black px-6 pt-6 pb-10 max-w-[620px] mx-auto text-center">
        <StackedLibrariesGlyph size={340} />
        <h2 className="text-[22px] font-bold leading-[1.2] text-black mt-5">
          Build your first Shared Library
        </h2>
        <p className="text-sm leading-[1.55] text-gray-600 mt-2.5 max-w-[460px]">
          Shared Libraries are collections of playlists, folders, and apps your
          whole team can use. Create it once, keep everyone in sync.
        </p>
        <div className="flex items-center gap-3 mt-7">
          <Button onPress={onNewPresentation}>
            {createButtonLabel ?? 'Create a Shared Library'}
          </Button>
          <a
            href={SHARED_LIBRARIES_HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-sm px-2 py-3"
          >
            Learn more about Libraries
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              aria-hidden
            >
              <path
                d="M1 5.5h8.5M6 2l3.5 3.5L6 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[490px] flex flex-1 flex-col items-center text-black">
      <Text className="text-xl font-bold mb-3 mt-[74px]">
        It's a little quiet in here
      </Text>
      <Text variant="caption">
        You can add folders, apps and more! Try some below:
      </Text>
      <div className="flex my-[46px] flex-wrap gap-6">
        <PresentationCard
          img={DisplaiAcademyThumbnail}
          label="Learn more about Libraries"
          onClick={() =>
            window.open(
              'https://help.displai.ai/getting-started/5imknKqrcfswGZ9RmtHfPK/dashboard-introduction-and-basics/5imknKqrcinJNiXdEoT8cC',
              '_blank',
            )
          }
        />
        <PresentationCard
          img={ImagesThumbnail}
          label="Create Pictures presentation"
          onClick={() => onFileUpload()}
        />
        {isLiteUser ? (
          <PresentationCard
            img={MenusThumbnail}
            label="Create Menus presentation"
            onClick={() => onPresentationCardClick(MENUS_APP_ID)}
          />
        ) : (
          <PresentationCard
            img={InstagramThumbnail}
            label="Create Instagram presentation"
            onClick={() => onPresentationCardClick(INSTAGRAM_APP_ID)}
          />
        )}
      </div>
      <Button onPress={onNewPresentation}>{createButtonLabel}</Button>
    </div>
  );
};

export default EmptyLibrary;
